import { useState } from "react";
import { Link } from "react-router-dom";
import { Ban, ClipboardList, CreditCard, Pencil, Printer, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, label, useCan } from "@/lib/api";
import { printReceipt } from "@/lib/receipt";
import { Empty, StatusBadge } from "@/components/common";
import ClinicalForm from "@/components/forms/ClinicalForm";
import { STATUSES } from "@/components/forms/TreatmentForm";

const PatientCell = ({ r }) => <Link to={`/patients/${r.patient_id}`} className="patient-link" data-testid={`row-patient-link-${r.id}`}>{r.patient_name}<small>{r.opd}</small></Link>;
const CLIN = [["chief_complaint", "Complaint"], ["examination", "Examination"], ["diagnosis", "Diagnosis"], ["radiographic_findings", "Radiographs"], ["procedures", "Procedures"], ["prescription", "Prescription"], ["notes", "Notes"]];

export function ClinicalList({ rows, onChanged, hidePatient }) {
  const can = useCan();
  const [edit, setEdit] = useState(null);
  if (!rows.length) return <Empty icon={ClipboardList} title="No clinical records yet" text="Write a visit note or approve a voice entry." testid="clinical-empty" />;
  return (
    <div className="record-list">
      {rows.map((r) => (
        <article className="record-item" key={r.id} data-testid={`clinical-record-${r.id}`}>
          <header>
            <div><p className="eyebrow">{r.visit_date}{r.tooth_numbers && ` · TOOTH ${r.tooth_numbers}`}</p>{!hidePatient && <PatientCell r={r} />}<h3>{r.diagnosis || r.chief_complaint || "Visit note"}</h3></div>
            <div className="row-actions">
              {r.source === "voice" && <span className="badge voice">voice</span>}
              {r.consent && <span className="badge completed">consent</span>}
              <button className="icon-btn" onClick={() => printNote(r)} data-testid={`print-clinical-${r.id}`} title="Print note"><Printer size={15} /></button>
              {can("clinical:write") && <button className="icon-btn" onClick={() => setEdit(r)} data-testid={`edit-clinical-${r.id}`} title="Edit"><Pencil size={15} /></button>}
            </div>
          </header>
          <dl>{CLIN.filter(([k]) => r[k]).map(([k, l]) => <div key={k}><dt>{l}</dt><dd>{r[k]}</dd></div>)}</dl>
          <footer>Recorded by {r.created_by_name || "team"}{r.updated_at && " · edited"}</footer>
        </article>
      ))}
      {edit && <ClinicalForm record={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />}
    </div>
  );
}

function printNote(r) {
  const w = window.open("", "_blank");
  if (!w) return toast.error("Allow pop-ups to print the note");
  const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  w.document.write(`<html><head><title>${esc(r.patient_name)} ${esc(r.visit_date)}</title><style>body{font-family:sans-serif;padding:40px;color:#21171b}h1{color:#762c39;margin:0}dt{font-weight:700;margin-top:14px;color:#762c39}dd{margin:4px 0 0}</style></head><body><h1>DentaflowAI · Clinical note</h1><p>${esc(r.patient_name)} · ${esc(r.opd)} · Visit ${esc(r.visit_date)}${r.tooth_numbers ? ` · Tooth ${esc(r.tooth_numbers)}` : ""}</p><dl>${CLIN.filter(([k]) => r[k]).map(([k, l]) => `<dt>${l}</dt><dd>${esc(r[k])}</dd>`).join("")}</dl><p>Consent: ${r.consent ? "taken" : "not recorded"}</p></body></html>`);
  w.document.close(); w.print();
}

export function TreatmentList({ rows, onChanged, hidePatient }) {
  const can = useCan();
  const setStatus = async (t, status) => {
    try { await api.patch(`/treatments/${t.id}`, { status }); toast.success(`Marked ${label(status)}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows.length) return <Empty icon={Stethoscope} title="No treatments yet" text="Add the agreed treatment plan to start the ledger." testid="treatments-empty" />;
  return (
    <section className="table-card">
      <div className={`table-head ${hidePatient ? "treat-grid-p" : "treat-grid"}`}>{!hidePatient && <span>Patient</span>}<span>Treatment</span><span>Date</span><span>Agreed</span><span>Status</span></div>
      {rows.map((t) => (
        <div className={`table-row ${hidePatient ? "treat-grid-p" : "treat-grid"}`} key={t.id} data-testid={`treatment-row-${t.id}`}>
          {!hidePatient && <PatientCell r={t} />}
          <span><b>{t.treatment}</b>{t.tooth && <small className="sub"> · tooth {t.tooth}</small>}</span>
          <span>{t.date}</span>
          <span className={t.status === "cancelled" ? "amount struck" : "amount"} data-testid={`treatment-amount-${t.id}`}>{inr(t.agreed_amount)}</span>
          {can("treatments:write")
            ? <select className={`status-select ${t.status}`} value={t.status} onChange={(e) => setStatus(t, e.target.value)} data-testid={`treatment-status-${t.id}`}>{STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select>
            : <StatusBadge value={t.status} testid={`treatment-status-${t.id}`} />}
        </div>
      ))}
    </section>
  );
}

export function TransactionList({ rows, onChanged, hidePatient }) {
  const can = useCan();
  const voidTx = async (t) => {
    const reason = window.prompt("Reason for voiding this entry (kept in the audit trail):");
    if (!reason) return;
    try { await api.post(`/payments/${t.id}/void`, { reason }); toast.success("Transaction voided"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows.length) return <Empty icon={CreditCard} title="No transactions yet" text="Payments, advances, discounts and refunds appear here." testid="payments-empty" />;
  return (
    <section className="table-card">
      <div className={`table-head ${hidePatient ? "tx-grid-p" : "tx-grid"}`}>{!hidePatient && <span>Patient</span>}<span>Type</span><span>Date</span><span>Amount</span><span>Method</span><span /></div>
      {rows.map((t) => (
        <div className={`table-row ${hidePatient ? "tx-grid-p" : "tx-grid"} ${t.status === "void" ? "voided" : ""}`} key={t.id} data-testid={`transaction-row-${t.id}`}>
          {!hidePatient && <PatientCell r={t} />}
          <span><StatusBadge value={t.status === "void" ? "void" : t.type} testid={`transaction-type-${t.id}`} />{t.receipt_no && <small className="sub block">{t.receipt_no}</small>}{t.notes && <small className="sub block">{t.notes}</small>}{t.void_reason && <small className="sub block">Void: {t.void_reason}</small>}</span>
          <span>{t.date}</span>
          <span className={`amount ${t.type}`} data-testid={`transaction-amount-${t.id}`}>{t.type === "discount" ? "−" : t.type === "refund" ? "↩ " : ""}{inr(t.amount)}</span>
          <span>{t.type === "discount" ? "—" : t.method}</span>
          <span className="row-actions">
            {t.status !== "void" && t.type !== "discount" && <button className="icon-btn" onClick={() => printReceipt(t.id)} title="Print receipt" data-testid={`print-receipt-${t.id}`}><Printer size={15} /></button>}
            {can("payments:void") && t.status !== "void" && <button className="icon-btn" onClick={() => voidTx(t)} title="Void" data-testid={`void-transaction-${t.id}`}><Ban size={15} /></button>}
          </span>
        </div>
      ))}
    </section>
  );
}
